/**
 * SupplierOnboardPage — /supplier/onboard
 *
 * 4-step wizard (< 2 min completion per your spec):
 *   1. Category (instant identity)
 *   2. Business name + postcode
 *   3. Description + photos (optional — progressive completion)
 *   4. Done — go to dashboard
 *
 * Saves to /api/suppliers/onboard after each step so progress never lost.
 * Handles Studio Pass auto-migration: if user already has a profile
 * with some fields filled, we resume from the earliest incomplete step.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, ArrowRight, ArrowLeft, Storefront, Camera, FloppyDisk } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => {
  try {
    const t = localStorage.getItem('session_token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch { return {}; }
};

const SupplierOnboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editMode = searchParams.get('edit') === '1';
  const [categories, setCategories] = useState([]);
  const [supplier, setSupplier] = useState(null);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    category: '', business_name: '', postcode: '', description: '', images: [], radius_miles: 50,
  });

  // Load categories + existing profile (Studio Pass auto-migration)
  useEffect(() => {
    // Bounce unauthenticated users to the supplier-flavoured login
    // rather than loading the page and toasting an error.
    const hasSession = (() => {
      try { return !!localStorage.getItem('session_token'); } catch { return false; }
    })();
    if (!hasSession) {
      navigate('/login?from=/supplier/onboard&role=supplier', { replace: true });
      return;
    }

    axios.get(`${API}/suppliers/categories`).then(r => setCategories(r.data.categories || []));
    axios.get(`${API}/suppliers/me`, { headers: authHeaders() })
      .then(r => {
        const s = r.data.supplier || {};
        setSupplier(s);
        setForm(f => ({
          ...f,
          category: s.category || '',
          business_name: s.business_name || '',
          postcode: s.postcode || '',
          description: s.description || '',
          images: s.images || [],
          radius_miles: s.radius_miles || 50,
        }));
        // In edit mode we always show the unified form — never resume
        // at a wizard step. Otherwise resume at the earliest incomplete
        // step (original onboarding behaviour).
        if (editMode) {
          setStep(0); // 0 = unified edit form
        } else if (!s.category) setStep(1);
        else if (!s.business_name || !s.postcode) setStep(2);
        else if (!s.is_live) setStep(3);
        else setStep(4);
      })
      .catch((err) => {
        // Token present but rejected (expired/invalid) → send to login, no toast spam.
        if (err?.response?.status === 401) {
          navigate('/login?from=/supplier/onboard&role=supplier', { replace: true });
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/suppliers/onboard`, patch, { headers: authHeaders() });
      setSupplier(r.data.supplier);
      return r.data.supplier;
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save');
      return null;
    } finally { setSaving(false); }
  };

  const onPickCategory = async (catId) => {
    setForm(f => ({ ...f, category: catId }));
    const s = await save({ category: catId });
    if (s) setStep(2);
  };

  const onSubmitBasics = async () => {
    if (!form.business_name.trim()) return toast.error('Business name required');
    if (!form.postcode.trim()) return toast.error('Postcode required');
    const s = await save({ business_name: form.business_name, postcode: form.postcode });
    if (s) setStep(3);
  };

  const onSubmitDetails = async () => {
    const s = await save({ description: form.description, images: form.images });
    if (s) setStep(4);
  };

  const onAddImageFile = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (list.length === 0) return;
    const remaining = Math.max(0, 10 - (form.images?.length || 0));
    if (remaining === 0) {
      toast.error('Maximum 10 photos. Remove one to add another.');
      return;
    }
    const accepted = list.slice(0, remaining);
    const skipped = list.length - accepted.length;
    setUploadingPhoto(true);
    let okCount = 0;
    let lastImages = form.images;
    for (const file of accepted) {
      if (!file.type?.startsWith('image/')) {
        toast.error(`${file.name}: not an image — skipped`);
        continue;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name}: over 10MB — skipped`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/suppliers/me/images`, fd, {
          headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        lastImages = r.data.images || lastImages;
        if (r.data.supplier) setSupplier(r.data.supplier);
        okCount += 1;
      } catch (err) {
        toast.error(err?.response?.data?.detail || `${file.name}: upload failed`);
      }
    }
    setForm(f => ({ ...f, images: lastImages }));
    setUploadingPhoto(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (okCount > 0) toast.success(`${okCount} photo${okCount > 1 ? 's' : ''} added${skipped ? ` · ${skipped} skipped (10 max)` : ''}`);
  };
  const onRemoveImage = async (idx) => {
    const url = form.images[idx];
    if (!url) return;
    // Optimistic UI — remove locally first
    setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
    try {
      await axios.delete(`${API}/suppliers/me/images`, {
        headers: authHeaders(),
        params: { url },
      });
    } catch {
      toast.error('Could not remove — refresh to try again');
    }
  };

  const onSaveEdits = async () => {
    if (!form.business_name.trim()) return toast.error('Business name required');
    if (!form.postcode.trim()) return toast.error('Postcode required');
    if (!form.category) return toast.error('Pick a category');
    const s = await save({
      category: form.category,
      business_name: form.business_name,
      postcode: form.postcode,
      description: form.description,
      radius_miles: form.radius_miles,
    });
    if (s) {
      toast.success('Profile saved');
      navigate('/dashboard/supplier');
    }
  };

  const progress = [
    { n: 1, label: 'Category' },
    { n: 2, label: 'Basics' },
    { n: 3, label: 'Profile' },
    { n: 4, label: 'Done' },
  ];

  return (
    <div className="min-h-screen bg-[#0F1014] text-white px-5 py-8" data-testid="supplier-onboard">
      <div className="max-w-xl mx-auto">
        {/* Edit mode header — replaces the wizard progress rail */}
        {editMode && step === 0 ? (
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => navigate('/dashboard/supplier')}
              className="flex items-center gap-1.5 text-white/60 hover:text-white text-xs font-bold"
              data-testid="edit-back-dashboard"
            >
              <ArrowLeft size={14} weight="bold" /> Dashboard
            </button>
            <h1 className="text-base font-black">Edit profile</h1>
            <span className="w-16" />
          </div>
        ) : (
          /* Progress rail */
          <div className="flex items-center justify-between mb-8">
            {progress.map((p, i) => (
              <React.Fragment key={p.n}>
                <div className="flex flex-col items-center gap-1 min-w-0">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black transition-all"
                    style={{
                      background: step >= p.n ? 'linear-gradient(135deg, #B89762, #C29D52)' : 'rgba(255,255,255,0.08)',
                      color: step >= p.n ? '#fff' : 'rgba(255,255,255,0.4)',
                    }}
                  >
                    {step > p.n ? <CheckCircle size={16} weight="fill" /> : p.n}
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: step >= p.n ? '#fff' : 'rgba(255,255,255,0.3)' }}>{p.label}</span>
                </div>
                {i < progress.length - 1 && (
                  <div className="flex-1 h-0.5 mx-1" style={{ background: step > p.n ? 'linear-gradient(90deg, #B89762, #C29D52)' : 'rgba(255,255,255,0.06)' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* STEP 0 — Unified edit form (only when ?edit=1) */}
        {editMode && step === 0 && (
          <div data-testid="edit-mode-form" className="space-y-6">
            {/* Category picker */}
            <div>
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider block mb-2">Category</span>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {categories.map(c => (
                  <button
                    key={c.id}
                    onClick={() => setForm(f => ({ ...f, category: c.id }))}
                    className="flex flex-col items-center gap-1 p-3 rounded-xl border transition-all"
                    style={{
                      background: form.category === c.id ? 'rgba(194,157,82,0.12)' : 'rgba(255,255,255,0.03)',
                      borderColor: form.category === c.id ? 'rgba(194,157,82,0.5)' : 'rgba(255,255,255,0.08)',
                    }}
                    data-testid={`edit-cat-${c.id}`}
                  >
                    <span className="text-2xl">{c.emoji}</span>
                    <span className="text-[11px] font-bold text-white text-center leading-tight">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Business name */}
            <label className="block">
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Business name</span>
              <input
                value={form.business_name}
                onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
                placeholder="Phoenix Beats DJ"
                className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-[#B89762]/40/50 outline-none"
                data-testid="edit-business-name"
              />
            </label>

            {/* Postcode */}
            <label className="block">
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Postcode</span>
              <input
                value={form.postcode}
                onChange={e => setForm(f => ({ ...f, postcode: e.target.value.toUpperCase() }))}
                placeholder="SW1A 1AA"
                className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-[#B89762]/40/50 outline-none"
                data-testid="edit-postcode"
              />
            </label>

            {/* Travel radius — controls customer search reach. Suppliers
               who travel nationally should set this near 200; local
               services keep it tight. */}
            <label className="block">
              <div className="flex items-baseline justify-between">
                <span className="text-white/60 text-xs font-bold uppercase tracking-wider">How far do you travel?</span>
                <span className="text-[#C29D52] text-sm font-black" data-testid="edit-radius-value">{form.radius_miles} mi</span>
              </div>
              <input
                type="range"
                min={1}
                max={200}
                step={1}
                value={form.radius_miles}
                onChange={e => setForm(f => ({ ...f, radius_miles: parseInt(e.target.value, 10) }))}
                className="mt-2 w-full accent-[#C29D52]"
                data-testid="edit-radius-slider"
              />
              <p className="text-[11px] text-white/45 mt-1">
                Customers searching within this distance from your postcode will see you.
                {form.radius_miles >= 150 && ' Set near 200 if you cover the whole UK.'}
              </p>
            </label>

            {/* Description */}
            <label className="block">
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider">About your business</span>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="What makes you special? Couples want a feel for your style."
                rows={4}
                className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-[#B89762]/40/50 outline-none resize-none"
                data-testid="edit-description"
              />
            </label>

            {/* Photos */}
            <div>
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Photos ({form.images.length}/10)</span>
              <p className="text-[#C29D52]/80 text-[11px] mt-1 font-bold">📷 Suppliers with photos get more enquiries.</p>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {form.images.map((src, i) => (
                  <div key={i} className="relative aspect-square rounded-lg bg-cover bg-center group" style={{ background: `url(${src}) center/cover` }} data-testid={`edit-img-${i}`}>
                    <button onClick={() => onRemoveImage(i)} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/70 text-white text-sm font-bold flex items-center justify-center" data-testid={`remove-img-${i}`}>×</button>
                  </div>
                ))}
                {form.images.length < 10 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="aspect-square rounded-lg bg-white/5 hover:bg-white/10 border border-dashed border-white/20 flex flex-col items-center justify-center gap-1 text-white/50 text-xs disabled:opacity-50"
                    data-testid="edit-add-image"
                  >
                    <Camera size={20} weight="bold" /> {uploadingPhoto ? 'Uploading…' : 'Add photos'}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => onAddImageFile(e.target.files)}
                  data-testid="edit-image-file-input"
                />
              </div>
            </div>

            {/* Save / View public */}
            <div className="flex gap-2 pt-2">
              {supplier?.id && supplier?.is_live && (
                <button
                  onClick={() => navigate(`/suppliers/${supplier.id}`)}
                  className="px-4 py-3 rounded-xl bg-white/5 text-white/70 text-sm font-bold border border-white/10"
                  data-testid="edit-view-public"
                >
                  View public
                </button>
              )}
              <button
                onClick={onSaveEdits}
                disabled={saving}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-1.5 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="edit-save"
              >
                {saving ? 'Saving…' : <><FloppyDisk size={14} weight="fill" /> Save changes</>}
              </button>
            </div>
          </div>
        )}

        {/* STEP 1 — Category */}
        {step === 1 && (
          <div data-testid="step-category">
            <h1 className="text-3xl font-black mb-2">What do you offer?</h1>
            <p className="text-white/50 text-sm mb-4">This helps customers find you.</p>
            <p className="text-[#C29D52] text-xs font-bold mb-6 flex items-center gap-1" data-testid="step1-hint">
              ⚡ Top suppliers reply quickly to enquiries and rank higher in search.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {categories.map(c => (
                <button
                  key={c.id}
                  onClick={() => onPickCategory(c.id)}
                  disabled={saving}
                  className="flex flex-col items-center gap-2 p-5 rounded-2xl bg-white/[0.03] hover:bg-white/[0.08] border border-white/10 hover:border-[#B89762]/40/50 transition-all disabled:opacity-50"
                  data-testid={`pick-cat-${c.id}`}
                >
                  <span className="text-4xl">{c.emoji}</span>
                  <span className="text-white font-bold text-sm">{c.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STEP 2 — Business basics */}
        {step === 2 && (
          <div data-testid="step-basics">
            <h1 className="text-3xl font-black mb-2">Tell us about your business</h1>
            <p className="text-white/50 text-sm mb-6">Just the essentials — you can polish the rest later.</p>

            <label className="block mb-4">
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Business name</span>
              <input
                value={form.business_name}
                onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
                placeholder="Phoenix Beats DJ"
                className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-[#B89762]/40/50 outline-none"
                data-testid="input-business-name"
              />
            </label>

            <label className="block mb-6">
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Postcode</span>
              <input
                value={form.postcode}
                onChange={e => setForm(f => ({ ...f, postcode: e.target.value.toUpperCase() }))}
                placeholder="SW1A 1AA"
                className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-[#B89762]/40/50 outline-none"
                data-testid="input-postcode"
              />
              <span className="text-white/30 text-xs mt-1 block">We use this to match you with events nearby.</span>
            </label>

            <div className="flex gap-2">
              <button onClick={() => setStep(1)} className="px-4 py-3 rounded-xl bg-white/5 text-white/70 text-sm font-bold flex items-center gap-1 border border-white/10" data-testid="back-to-category">
                <ArrowLeft size={14} weight="bold" /> Back
              </button>
              <button
                onClick={onSubmitBasics}
                disabled={saving}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="continue-basics"
              >
                {saving ? 'Saving…' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {/* STEP 3 — Profile polish */}
        {step === 3 && (
          <div data-testid="step-profile">
            <h1 className="text-3xl font-black mb-2">Make it pop</h1>
            <p className="text-white/50 text-sm mb-6">Optional — you can skip this and add it later.</p>

            <label className="block mb-4">
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Short description</span>
              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="3 lines about what makes you special…"
                rows={4}
                className="mt-1 w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-[#B89762]/40/50 outline-none resize-none"
                data-testid="input-description"
              />
            </label>

            <div className="mb-6">
              <span className="text-white/60 text-xs font-bold uppercase tracking-wider">Photos (up to 10)</span>
              <p className="text-[#C29D52]/80 text-[11px] mt-1 font-bold" data-testid="step3-photo-hint">
                📷 Suppliers with photos get more messages.
              </p>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {form.images.map((src, i) => (
                  <div key={i} className="relative aspect-square rounded-lg bg-cover bg-center group" style={{ background: `url(${src}) center/cover` }} data-testid={`onboard-img-${i}`}>
                    <button onClick={() => onRemoveImage(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white text-xs opacity-100 sm:opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                  </div>
                ))}
                {form.images.length < 10 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingPhoto}
                    className="aspect-square rounded-lg bg-white/5 hover:bg-white/10 border border-dashed border-white/20 flex flex-col items-center justify-center gap-1 text-white/50 text-xs disabled:opacity-50"
                    data-testid="add-image"
                  >
                    <Camera size={20} weight="bold" /> {uploadingPhoto ? 'Uploading…' : 'Add'}
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => onAddImageFile(e.target.files)}
                  data-testid="image-file-input"
                />
              </div>
            </div>

            <div className="flex gap-2">
              <button onClick={onSubmitDetails} className="px-4 py-3 rounded-xl bg-white/5 text-white/70 text-sm font-bold border border-white/10" data-testid="skip-details">Skip for now</button>
              <button
                onClick={onSubmitDetails}
                disabled={saving}
                className="flex-1 py-3 rounded-xl text-sm font-bold text-white flex items-center justify-center gap-1 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="finish-details"
              >
                {saving ? 'Saving…' : <>Finish <ArrowRight size={14} weight="bold" /></>}
              </button>
            </div>
          </div>
        )}

        {/* STEP 4 — Done (Go Live moment + first action push) */}
        {step === 4 && (
          <div className="text-center py-8" data-testid="step-done">
            <div
              className="mx-auto mb-5 w-20 h-20 rounded-full flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)', boxShadow: '0 20px 60px rgba(194,157,82,0.35)' }}
            >
              <Storefront size={36} weight="fill" className="text-white" />
            </div>
            <h1 className="text-3xl font-black mb-2">You're live 🎉</h1>
            <p className="text-white/60 text-sm mb-5">Customers can now find you in search.</p>

            {/* Ranking education + first-action setup */}
            <div className="rounded-2xl p-4 mb-6 text-left border border-[#B89762]/40/20 bg-gradient-to-br from-[#B89762]/15 to-[#B89762]/10">
              <p className="text-[10px] font-black uppercase tracking-[2px] text-[#C29D52] mb-2">How ranking works</p>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2"><span>⚡</span><span>Fast replies rank higher in search</span></li>
                <li className="flex items-start gap-2"><span>🏆</span><span>More bookings = more enquiries</span></li>
                <li className="flex items-start gap-2"><span>🔥</span><span>Stay active to appear this week</span></li>
              </ul>
            </div>

            <div className="flex flex-col gap-2">
              <button
                onClick={() => navigate('/dashboard/supplier/leads')}
                className="w-full py-3.5 rounded-xl text-sm font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #B89762, #C29D52)' }}
                data-testid="go-leads"
              >
                View my leads
              </button>
              <button
                onClick={() => navigate('/dashboard/supplier')}
                className="w-full py-2.5 rounded-xl text-xs font-bold text-white/60 bg-white/5 border border-white/10 hover:text-white"
                data-testid="go-dashboard"
              >
                Go to dashboard instead
              </button>
              <button
                onClick={() => supplier?.id ? navigate(`/suppliers/${supplier.id}`) : navigate('/suppliers')}
                className="w-full py-2 text-xs text-white/40 hover:text-white/70"
                data-testid="view-profile"
              >
                View my public profile
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierOnboardPage;
